import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
	User, LoginResp, Quote,
	OrdersByCOResp, initUser,
	initLoginResp, UserName, PasswordResp
} from "../types.d";
import {
	errorState, userState, shippedRespState,
	openRespState, updateSuccessState
} from '../state/state'

import { useNavigate } from "react-router-dom";
import axios from 'axios'
import { getApiBaseUrl, API_ENDPOINTS } from './api.config'


// Only one /OpenOrders search is ever in flight; a newer one aborts the older
// so abandoned requests can't surface a timeout error long after their results
// were superseded (Node-RED only answers the newest overlapping request).
let openOrdersAbort: AbortController | null = null;


///////////////////////////////////////////////////////////////////////////////////////////////////////
//	useAxiosWrapper
///////////////////////////////////////////////////////////////////////////////////////////////////////
function useAxiosWrapper() {
	const user = useRecoilValue(userState);
	const [error, setError] = useRecoilState(errorState);
	const navigate = useNavigate();
	const setShippedResp = useSetRecoilState(shippedRespState);
	const setOpenResp = useSetRecoilState(openRespState);
	const setUpdateSuccess = useSetRecoilState(updateSuccessState);


	const axiosInstance = axios.create({
		baseURL: getApiBaseUrl(),
		timeout: 100000,
		headers: { 'Authorization': `Bearer ${user?.token}` }
	});


	return {
		login,
		getQuotes,
		getOpenOrdersByQ,
		getOpenOrdersCO,
		getOpenOrdersByPO,
		getUserList,
		getPW,
		updateUser,
		getUserInfo
	};


	///////////////////////////////////////////////////////////////////////////////////////////////////////
	//	login
	///////////////////////////////////////////////////////////////////////////////////////////////////////
	async function login(user: User): Promise<LoginResp> {

		let url = API_ENDPOINTS.login;

		let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: false }
		setError(newError);

		setShippedResp(false);
		setOpenResp(false);

		const response = await axiosInstance.post(url, user).catch(function (error) {
			if (error.response) {
				// The request was made and the server responded with a status code
				// that falls out of the range of 2xx
				console.log(error.response.data);
				console.log(error.response.status);
				console.log(error.response.headers);

				if (error.response.status == 401) {
					navigate("/");
				}
			} else if (error.request) {
				// The request was made but no response was received
				// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
				// http.ClientRequest in node.js
				console.log(error.request);
			} else {
				// Something happened in setting up the request that triggered an Error
				console.log('Error', error.message);
			}

			let newError = { ...error, isError: true, message: error.message, requestSent: false }
			setError(newError);
			console.log(error.config);
		});;

		if (response) {
			let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: true }
			setError(newError);
			return response.data;
		}
		else {
			return initLoginResp();
		}
	}


	///////////////////////////////////////////////////////////////////////////////////////////////////////
	//	getQuotes
	///////////////////////////////////////////////////////////////////////////////////////////////////////
	async function getQuotes(soldname: string, cuspo: string, custno: string[], qnumber: string = '', startDate: string = '', endDate: string = ''): Promise<Quote[]> {

		let url = API_ENDPOINTS.quotes;

		let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: false }
		setError(newError);

		// Convert array to comma-separated string for backend compatibility
		const custnoStr = custno.join(',');

		const response = await axiosInstance.post(url, { soldname, cuspo, custno: custnoStr, qnumber, startDate, endDate, internal:true }).catch(function (error) {
			if (error.response) {
				// The request was made and the server responded with a status code
				// that falls out of the range of 2xx
				console.log(error.response.data);
				console.log(error.response.status);
				console.log(error.response.headers);

				if (error.response.status == 401) {
					navigate("/");
				}
			} else if (error.request) {
				// The request was made but no response was received
				// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
				// http.ClientRequest in node.js1
				console.log(error.request);
			} else {
				// Something happened in setting up the request that triggered an Error
				console.log('Error', error.message);
			}

			let newError = { ...error, isError: true, message: error.message, requestSent: false }
			setError(newError);
			console.log(error.config);
		});;

		if (response) {
			let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: true }
			setError(newError);
			return response.data
		}
		else {
			return [];
		}
	}

	///////////////////////////////////////////////////////////////////////////////////////////////////////
	//	getOpenOrdersByQ
	///////////////////////////////////////////////////////////////////////////////////////////////////////
	async function getOpenOrdersByQ(qNumber: string): Promise<OrdersByCOResp> {
		return getOrdersBySystemLink({ searchField: 'orderReference', searchValue: qNumber });
	}


	///////////////////////////////////////////////////////////////////////////////////////////////////////
	//	getOpenOrdersCO
	///////////////////////////////////////////////////////////////////////////////////////////////////////
	async function getOpenOrdersCO(custCO: string): Promise<OrdersByCOResp> {
		return getOrdersBySystemLink({ searchField: 'order', searchValue: ' ' + custCO });
	}


	///////////////////////////////////////////////////////////////////////////////////////////////////////
	//	getOpenOrdersByPO
	///////////////////////////////////////////////////////////////////////////////////////////////////////
	async function getOpenOrdersByPO(cusPO: string): Promise<OrdersByCOResp> {
		return getOrdersBySystemLink({ searchField: 'relatedSalesOrderExtension.purchaseOrder', searchValue: cusPO });
	}


	///////////////////////////////////////////////////////////////////////////////////////////////////////
	//	getOrdersBySystemLink (shared by Q + CO + PO searches)
	//	- custNos scopes the search to the logged-in user's customer numbers on the backend
	///////////////////////////////////////////////////////////////////////////////////////////////////////
	async function getOrdersBySystemLink(req: { searchField: string; searchValue: string }): Promise<OrdersByCOResp> {

		let url = API_ENDPOINTS.openOrders;

		if (openOrdersAbort) openOrdersAbort.abort();
		const controller = new AbortController();
		openOrdersAbort = controller;

		let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: false }
		setError(newError);
		setOpenResp(false);
		setShippedResp(false);

		let canceled = false;

		const response = await axiosInstance.post(url, { ...req, custNos: user?.custNo || [], internal: true }, { signal: controller.signal }).catch(function (err) {
			if (axios.isCancel(err)) {
				// superseded by a newer search — stay quiet, the caller discards this result
				canceled = true;
				return;
			}

			if (err.response) {
				console.log(err.response.data);
				console.log(err.response.status);
				console.log(err.response.headers);
			} else if (err.request) {
				console.log(err.request);
			} else {
				console.log('Error', err.message);
			}

			let newError = { ...error, isError: true, message: err.message, requestSent: false }
			setError(newError);
			console.log(err.config);
		});

		if (openOrdersAbort == controller) openOrdersAbort = null;

		if (canceled) {
			return { openOrders: [], shippedOrders: [], canceled: true };
		}

		setOpenResp(true);
		setShippedResp(true);

		if (response) {
			let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: true }
			setError(newError);
			return response.data
		}
		else {
			return { openOrders: [], shippedOrders: [] };
		}
	}


	///////////////////////////////////////////////////////////////////////////////////////////////////////
	//	getUserList
	///////////////////////////////////////////////////////////////////////////////////////////////////////
	async function getUserList(): Promise<UserName[]> {

		let url = API_ENDPOINTS.users;

		let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: false }
		setError(newError);

		const response = await axiosInstance.post(url, { internal: true }).catch(function (error) {
			if (error.response) {
				// The request was made and the server responded with a status code
				// that falls out of the range of 2xx
				console.log(error.response.data);
				console.log(error.response.status);
				console.log(error.response.headers);

			} else if (error.request) {
				// The request was made but no response was received
				// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
				// http.ClientRequest in node.js
				console.log(error.request);
			} else {
				// Something happened in setting up the request that triggered an Error
				console.log('Error', error.message);
			}

			let newError = { ...error, isError: true, message: error.message, requestSent: false }
			setError(newError);
			console.log(error.config);
		});;

		if (response) {
			let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: true }
			setError(newError);
			return response.data
		}
		else {
			return [];
		}
	}


	///////////////////////////////////////////////////////////////////////////////////////////////////////
	//	getPW
	///////////////////////////////////////////////////////////////////////////////////////////////////////
	async function getPW(username: string): Promise<PasswordResp> {

		let url = API_ENDPOINTS.password;

		let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: false }
		setError(newError);

		const response = await axiosInstance.post(url, { username, internal: true }).catch(function (error) {
			if (error.response) {
				// The request was made and the server responded with a status code
				// that falls out of the range of 2xx
				console.log(error.response.data);
				console.log(error.response.status);
				console.log(error.response.headers);

			} else if (error.request) {
				// The request was made but no response was received
				// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
				// http.ClientRequest in node.js
				console.log(error.request);
			} else {
				// Something happened in setting up the request that triggered an Error
				console.log('Error', error.message);
			}

			let newError = { ...error, isError: true, message: error.message, requestSent: false }
			setError(newError);
			console.log(error.config);
		});;

		if (response) {
			let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: true }
			setError(newError);
			return response.data
		}
		else {
			return { password: '' }
		}
	}


	///////////////////////////////////////////////////////////////////////////////////////////////////////
	//	getUserInfo
	///////////////////////////////////////////////////////////////////////////////////////////////////////
	async function getUserInfo(username: string): Promise<User> {

		let url = API_ENDPOINTS.userInfo;

		let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: false }
		setError(newError);

		const response = await axiosInstance.post(url, { username, internal: true }).catch(function (error) {
			if (error.response) {
				// The request was made and the server responded with a status code
				// that falls out of the range of 2xx
				console.log(error.response.data);
				console.log(error.response.status);
				console.log(error.response.headers);

			} else if (error.request) {
				// The request was made but no response was received
				// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
				// http.ClientRequest in node.js
				console.log(error.request);
			} else {
				// Something happened in setting up the request that triggered an Error
				console.log('Error', error.message);
			}

			let newError = { ...error, isError: true, message: error.message, requestSent: false }
			setError(newError);
			console.log(error.config);
		});;

		if (response) {
			let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: true }
			setError(newError);

			// Normalize custNo to array
			const userData = response.data;
			if (userData.custNo && typeof userData.custNo === 'string') {
				userData.custNo = userData.custNo.split(',').map((n: string) => n.trim()).filter((n: string) => n !== '');
			}

			return userData;
		}
		else {
			let user = initUser();
			return user;
		}
	}


	///////////////////////////////////////////////////////////////////////////////////////////////////////
	//	updateUser
	///////////////////////////////////////////////////////////////////////////////////////////////////////
	async function updateUser(user: User): Promise<boolean> {

		let url = API_ENDPOINTS.updateLogin;

		let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: false }
		setError(newError);

		setUpdateSuccess(false);

		// Convert custNo array to comma-separated string for backend
		const userToSend = {
			...user,
			custNo: Array.isArray(user.custNo) ? user.custNo.join(',') : user.custNo
		};

		const response = await axiosInstance.post(url, { user: userToSend, internal: true }).catch(function (error) {
			if (error.response) {
				// The request was made and the server responded with a status code
				// that falls out of the range of 2xx
				console.log(error.response.data);
				console.log(error.response.status);
				console.log(error.response.headers);

			} else if (error.request) {
				// The request was made but no response was received
				// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
				// http.ClientRequest in node.js
				console.log(error.request);
			} else {
				// Something happened in setting up the request that triggered an Error
				console.log('Error', error.message);
			}

			let newError = { ...error, isError: true, message: error.message, requestSent: false }
			setError(newError);
			console.log(error.config);
		});;

		if (response) {
			let newError = { ...error, isError: false, status: "NA", message: "NA", requestSent: true }
			setError(newError);
			setUpdateSuccess(true);

			return true;
		}
		else {
			setUpdateSuccess(false);

			return false;
		}
	}
}

export {
	useAxiosWrapper
};